import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { Toaster } from "@/components/ui/sonner";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";

import appCss from "../styles.css?url";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "legaldecoder — Understand Your Contract Before You Sign" },
      {
        name: "description",
        content:
          "legaldecoder turns dense leases, NDAs, and freelance contracts into plain English. Spot red flags and sign with confidence in seconds.",
      },
      { name: "author", content: "legaldecoder" },
      { name: "google-adsense-account", content: "ca-pub-3575836905623360" },
      { name: "google-site-verification", content: "0Irh2H0247nDH1osnpor4Zhda_x2UJTQNVqUhTmW7V4" },
      { property: "og:title", content: "legaldecoder — Decode Any Contract in Seconds" },
      {
        property: "og:description",
        content:
          "legaldecoder translates leases, NDAs, and freelance contracts into plain English, flags risky clauses, and helps you sign with confidence.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://legaldecoder.io/og-image.png" },
      { property: "og:site_name", content: "legaldecoder" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://legaldecoder.io/og-image.png" },
      { name: "twitter:title", content: "legaldecoder — Decode Any Contract in Seconds" },
      {
        name: "twitter:description",
        content:
          "legaldecoder translates leases, NDAs, and freelance contracts into plain English, flags risky clauses, and helps you sign with confidence.",
      },
    ],
    links: [
      { rel: "preload", href: appCss, as: "style" },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@600;700;800&display=swap",
      },
      {
        rel: "preconnect",
        href: "https://pagead2.googlesyndication.com",
        crossOrigin: "anonymous",
      },
      { rel: "dns-prefetch", href: "https://pagead2.googlesyndication.com" },
    ],
    scripts: [
      {
        src: "https://www.googletagmanager.com/gtag/js?id=G-1GF3WJ6BJV",
        async: true,
      },
      {
        children: `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'G-1GF3WJ6BJV');`,
      },
      {
        // Apply persisted/system theme before paint to avoid FOUC.
        children: `(function(){try{var s=localStorage.getItem('ld-theme');var t=(s==='light'||s==='dark')?s:(window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light');var r=document.documentElement;r.classList.toggle('dark',t==='dark');r.setAttribute('data-theme',t);r.style.colorScheme=t;}catch(e){}})();`,
      },
      {
        // Defer AdSense loader until the page is idle to keep it off the
        // critical path. This significantly reduces main-thread script
        // evaluation/parse time during initial load without affecting ads,
        // which mount after first paint anyway.
        children: `(function(){function l(){if(window.__adsLoaded)return;window.__adsLoaded=true;var s=document.createElement('script');s.async=true;s.crossOrigin='anonymous';s.src='https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3575836905623360';document.head.appendChild(s);}var fired=false;function trigger(){if(fired)return;fired=true;('requestIdleCallback' in window)?requestIdleCallback(l,{timeout:5000}):setTimeout(l,200);}var evs=['scroll','keydown','mousemove','touchstart','click'];function on(){evs.forEach(function(e){window.removeEventListener(e,on,{passive:true});});trigger();}evs.forEach(function(e){window.addEventListener(e,on,{passive:true,once:true});});setTimeout(trigger,8000);})();`,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <>
      <PaymentTestModeBanner />
      <Outlet />
      <Toaster />
    </>
  );
}
